<script setup>
import { computed, ref, onMounted } from "vue";
import { useActions } from "vuex-composition-helpers";
import { useRoute, useRouter } from "vue-router/composables";
import _ from "lodash";

import JobAndCampaignSelector from "./JobAndCampaignSelector";
import ProjectsApi from "@/common/http/ProjectsApi";

const emit = defineEmits(["change", "loaded", "loading"]);

defineExpose({
  clearSelection,
});

const props = defineProps({
  defaultProjectId: {
    type: Number,
    default: null,
  },
  defaultProjectCampaignId: {
    type: Number,
    default: null,
  },
  projectSelectionDisabled: {
    type: Boolean,
    default: false,
  },
  disabledProjectIds:  {
    type: Array,
    default: () => [],
  },
  showCampaigns: {
    type: Boolean,
    default: true,
  },
  hideCreateNewJob: {
    type: Boolean,
    default: false,
  },
  showCampaignsOnlyForDefaultProject: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: "Choose a job or campaign",
  },
  cacheEnabled: {
    type: Boolean,
    default: true,
  },
  mineOnly: {
    type: Boolean,
    default: true,
  },
});

const {
  refreshCurrentUser,
} = useActions([
  "refreshCurrentUser",
]);

const route = useRoute();
const router = useRouter();
const isLoading = ref(false);
const searchedProjects = ref([]);
const searchedCampaigns = ref([]);
const currentSearchQuery = ref("");
const currentMineOnly = ref(props.mineOnly);
const currentPage = ref(1);
const totalCount = ref(0);
const isSearching = ref(false);
const isLoadingMore = ref(false);
const jobAndCampaignSelector = ref(null);

const PAGE_SIZE = 30;
let searchRequestId = 0;

const hasMore = computed(() => searchedProjects.value.length < totalCount.value);

const projectCampaignsList = computed(() => {
  return {
    projects: searchedProjects.value,
    campaigns: searchedCampaigns.value,
  };
});

const computedMineOnly = computed(() => {
  if (props.projectSelectionDisabled) {
    return false;
  }
  return props.mineOnly;
});

async function searchProjects(query, page = 1, { append = false, showLoading = false } = {}) {
  const requestId = ++searchRequestId;
  if (showLoading) {
    isSearching.value = true;
  }
  if (append) {
    isLoadingMore.value = true;
  }
  try {
    const params = {
      query: query || "",
      mineOnly: currentMineOnly.value,
      page,
      pageSize: PAGE_SIZE,
      includeProjectId: props.defaultProjectId,
    };
    if (props.showCampaigns) {
      params.includeCampaigns = true;
    }
    const { data } = await ProjectsApi.searchForSourcing(params);
    if (requestId !== searchRequestId) return;
    if (append) {
      const existingIds = new Set(searchedProjects.value.map(p => p.id));
      const newProjects = data.projects.filter(p => !existingIds.has(p.id));
      searchedProjects.value = [...searchedProjects.value, ...newProjects];
      if (data.campaigns) {
        const existingCampaignIds = new Set(searchedCampaigns.value.map(c => c.id));
        const newCampaigns = data.campaigns.filter(c => !existingCampaignIds.has(c.id));
        searchedCampaigns.value = [...searchedCampaigns.value, ...newCampaigns];
      }
    } else {
      searchedProjects.value = data.projects;
      if (data.campaigns) {
        searchedCampaigns.value = data.campaigns;
      } else {
        searchedCampaigns.value = [];
      }
    }
    if (page === 1) {
      totalCount.value = data.totalCount;
    }
    currentPage.value = page;
  } finally {
    if (requestId === searchRequestId) {
      isSearching.value = false;
      isLoadingMore.value = false;
    }
  }
}

const debouncedSearchProjects = _.debounce((query) => {
  searchProjects(query);
}, 300);

function onSearch(query) {
  currentSearchQuery.value = query;
  debouncedSearchProjects(query);
}

async function onMineOnlyChange(mineOnly) {
  currentMineOnly.value = mineOnly;
  await searchProjects(currentSearchQuery.value);
  // Re-evaluate the empty state after the mine-only fallback re-fetches: when the
  // mine-only search is empty the selector flips mineOnly off and re-searches, so
  // "No jobs!" must reflect the fallback results, not the initial empty mine list.
  if (!currentSearchQuery.value) {
    emit("loaded", { hasResults: searchedProjects.value.length > 0 });
  }
}

function onLoadMore() {
  if (hasMore.value && !isLoadingMore.value) {
    searchProjects(currentSearchQuery.value, currentPage.value + 1, { append: true });
  }
}

async function init() {
  refreshCurrentUser();

  isLoading.value = true;
  emit("loading");

  // Capture before the search: the child's empty-results watch can flip mine-only
  // off (mutating currentMineOnly) while this await is in flight, so reading it
  // afterward would misjudge whether the first pass was mine-only.
  const startedMineOnly = currentMineOnly.value;
  await searchProjects("", 1, { showLoading: true });
  isLoading.value = false;

  // An empty mine-only first pass auto-flips mine-only off and refetches (see the
  // projectsCampaignsData watch in JobAndCampaignSelector). Defer "loaded" to that
  // fallback's onMineOnlyChange so the "No jobs!" state doesn't flash before it resolves.
  const fallbackPending =
    !props.projectSelectionDisabled &&
    startedMineOnly &&
    searchedProjects.value.length === 0;
  if (!fallbackPending) {
    emit("loaded", { hasResults: searchedProjects.value.length > 0 });
  }
}

function projectFromCurrentSelection(projectOrCampaign) {
  if (projectOrCampaign.campaigns) { // Only jobs have the key "campaigns"
    return projectOrCampaign;

  } else if (props.defaultProjectId) {
    return searchedProjects.value.find(item => item.id === props.defaultProjectId);

  } else if (projectOrCampaign.projectId) { // Find job based on the campaign.projectId
    return searchedProjects.value.find(item => item.id === projectOrCampaign.projectId);
  }

  return null;
}

function campaignFromCurrentSelection(projectOrCampaign) {
  if (projectOrCampaign.projectId || projectOrCampaign.id === "save") {
    return projectOrCampaign;
  }

  return null;
}

function projectCampaignSelectorComponentChangeHandler(selectedProjectCampaign) {
  if (selectedProjectCampaign) {
    localStorage.setItem("project-campaign-selection", JSON.stringify(selectedProjectCampaign));
    emit("change", {
      project: projectFromCurrentSelection(selectedProjectCampaign),
      campaign: campaignFromCurrentSelection(selectedProjectCampaign),
    })

  } else {
    localStorage.removeItem("previousSelectedProjectCampaign");
    localStorage.removeItem("project-campaign-selection");
    emit("change", { campaign: null, project: null });
  }
}

function showNewProjectModal() {
  const query = Object.assign({}, route.query);
  query.parentModal = query.modal;
  query.modal = "new-project";

  router.push({ query });
}

function clearSelection() {
  jobAndCampaignSelector.value.clearSelection();
};

onMounted(() => {
  init();
});

</script>

<template>
  <div id="project-and-campaign-selector">
    <div v-if="!projectSelectionDisabled" class="d-flex justify-content-end">
      <el-button v-if="!hideCreateNewJob" class="create-new-project" type="text" @click="showNewProjectModal">
        <span class="material-icons-round">add</span>Create a new job
      </el-button>
    </div>

    <div class="d-flex align-items-center">
      <JobAndCampaignSelector
        ref="jobAndCampaignSelector"
        :projects-campaigns-data="projectCampaignsList"
        :loading="isSearching"
        :show-campaigns="showCampaigns"
        :placeholder="placeholder"
        :show-campaigns-only-for-default-project="showCampaignsOnlyForDefaultProject"
        :default-project-id="defaultProjectId"
        :default-project-campaign-id="defaultProjectCampaignId"
        :disabled-project-ids="disabledProjectIds"
        :project-selection-disabled="projectSelectionDisabled"
        :cache-enabled="cacheEnabled"
        :mine-only="computedMineOnly"
        :has-more="hasMore"
        @change="projectCampaignSelectorComponentChangeHandler"
        @search="onSearch"
        @mine-only-change="onMineOnlyChange"
        @load-more="onLoadMore"
      >
        <slot></slot>
      </JobAndCampaignSelector>
    </div>
  </div>
</template>

<style lang="scss" scoped>
#project-and-campaign-selector {
  ::v-deep .el-input__prefix {
    margin-left: 4px;
  }

  .create-new-project {
    text-align: right;
    margin-bottom: -8px;
    margin-right: 8px;

    &:hover {
      text-decoration: underline;
    }
  }
}

</style>
